/**
 * Text derived from chat messages: the readable text of a markdown answer
 * (the screen-reader announcement of a finished answer, the copy button)
 * and the question an answer replied to (Retry). Framework-free (copied to
 * next-mapit as TS).
 */

import { parseMarkdown } from "./markdown";
import { MESSAGE_ROLES } from "./protocol";

/** Link text that is already the URL (a bare / autolinked URL). */
const URL_TEXT = /^https?:\/\//i;

/**
 * @param {{ href: string }} link
 * @param {string} text the link's own text
 * @param {{ linkUrls?: boolean }} options
 */
const linkText = (link, text, options) => {
  if (!options.linkUrls || URL_TEXT.test(text.trim())) return text;
  return text.trim() ? `${text} (${link.href})` : link.href;
};

/**
 * @param {import("./markdown").InlineNode[]} nodes
 * @param {{ linkUrls?: boolean }} options
 */
const inlineText = (nodes, options) =>
  nodes
    .map((node) => {
      switch (node.type) {
        case "text":
        case "code":
          return node.value;
        case "br":
          return "\n";
        case "strong":
        case "em":
          return inlineText(node.children, options);
        case "link":
          return linkText(node, inlineText(node.children, options), options);
        default:
          return "";
      }
    })
    .join("");

/**
 * @param {import("./markdown").ListNode} list
 * @param {number} depth
 * @param {{ linkUrls?: boolean }} options
 * @returns {string[]} one line per item, nested items indented
 */
const listLines = (list, depth, options) =>
  list.items.flatMap((item, index) => {
    const marker = list.ordered ? `${(list.start ?? 1) + index}. ` : "- ";
    const indent = "  ".repeat(depth);
    const inline = item.children.filter((child) => child.type !== "list");
    const nested = item.children.filter((child) => child.type === "list");
    return [
      `${indent}${marker}${inlineText(inline, options)}`,
      ...nested.flatMap((child) => listLines(child, depth + 1, options)),
    ];
  });

/**
 * @param {import("./markdown").BlockNode} block
 * @param {{ linkUrls?: boolean }} options
 */
const blockText = (block, options) =>
  block.type === "list"
    ? listLines(block, 0, options).join("\n")
    : inlineText(block.children, options);

/**
 * The text a reader sees in a rendered answer: no markdown markers, links
 * as their text (a disallowed link is already plain text in the AST), list
 * items on their own lines, blocks separated by a blank line.
 *
 * `linkUrls` (the copy button) also keeps where each link goes: «text
 * (https://…)», unless the text already is the URL. The URL is the one the
 * rendered link opens (allowed and normalized by the link policy).
 *
 * @param {unknown} markdown
 * @param {{ linkUrls?: boolean }} [options]
 * @returns {string} "" for empty or non-string input
 */
export function toPlainText(markdown, options = {}) {
  return parseMarkdown(markdown)
    .children.map((block) => blockText(block, options))
    .filter((text) => text.trim() !== "")
    .join("\n\n");
}

/**
 * The user message an answer replied to: the closest user message before
 * it. Retry re-sends this text.
 *
 * @param {Array<{ id: string, role: string, content: string }>} messages oldest first
 * @param {string} messageId the answer
 * @returns {string} "" when the answer isn't found or nothing precedes it
 */
export function findQuestion(messages, messageId) {
  const index = messages.findIndex((message) => message.id === messageId);
  for (let i = index - 1; i >= 0; i -= 1) {
    if (messages[i].role === MESSAGE_ROLES.USER) return messages[i].content;
  }
  return "";
}
